import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import NoteCard from "../components/NoteCard";
import HashTag from "../components/HashTag";
import { notesWithTag, tagCounts } from "../data/notes";

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function TagPage() {
  const { tag } = useParams();
  const items = notesWithTag(tag);
  const allTags = tagCounts();

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
      className="py-10 md:py-14 px-4 md:px-8"
    >
      <Helmet>
        <title>#{tag} | Soham Desai</title>
        <meta name="description" content={`Notes tagged #${tag}.`} />
      </Helmet>

      <div className="max-w-6xl mx-auto">
        <motion.div variants={fade}>
          <Link
            to="/projects"
            className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-6 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Projects
          </Link>
        </motion.div>

        <motion.h1
          variants={fade}
          className="font-display text-3xl md:text-4xl font-semibold text-foreground border-l-2 border-accent pl-4 mb-4"
        >
          #{tag}
        </motion.h1>
        <motion.p variants={fade} className="text-muted-foreground mb-10">
          {items.length} {items.length === 1 ? "note" : "notes"} tagged{" "}
          <span className="text-accent">#{tag}</span>.
        </motion.p>

        {items.length > 0 ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((note) => (
              <NoteCard key={`${note.block}-${note.slug}`} note={note} showBlock />
            ))}
          </div>
        ) : (
          <motion.div
            variants={fade}
            className="rounded-lg border border-dashed border-border bg-surface/40 p-10 text-center"
          >
            <p className="text-muted">No notes with this tag.</p>
          </motion.div>
        )}

        {/* All tags */}
        <section className="mt-16">
          <motion.h2
            variants={fade}
            className="font-display text-xl font-semibold text-foreground mb-4"
          >
            All tags
          </motion.h2>
          <motion.div variants={fade} className="flex flex-wrap gap-2">
            {allTags.map(([t, count]) => (
              <span key={t} className="inline-flex items-center">
                <HashTag tag={t} variant="chip" />
                <span className="ml-1 mr-1 text-muted-foreground text-xs">{count}</span>
              </span>
            ))}
          </motion.div>
        </section>
      </div>
    </motion.div>
  );
}
